import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink } from 'lucide-react';
import { Citation } from '../../services/chatbot.service';
import { formatCitationDate, formatTipoLabel, safeExternalUrl } from '../../utils/citationFormat';

const OPEN_DELAY_MS = 250;
const CLOSE_DELAY_MS = 150;
const CARD_WIDTH = 344;
const EDGE = 8;
const GAP = 6;
// Respaldo sin medidas de layout (jsdom): sobre este largo el resumen pasa de 3 líneas a 12,5 px en 312 px.
const RESUMEN_LARGO = 150;

interface CitationLinkProps {
  citation: Citation;
  index: number;
  onClick: (citation: Citation) => void;
}

type Pos = { left: number; top?: number; bottom?: number };

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Foco al elemento que sigue al número en el orden de la página (la ficha vive al final del body, en un portal). */
function focusAfter(el: HTMLElement | null, card: HTMLElement | null) {
  if (!el) return;
  const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(x => !card?.contains(x));
  all[all.indexOf(el) + 1]?.focus();
}

/**
 * Número de cita en pastilla gris y su ficha (M-7, maqueta 2026-10-14-modos-respuesta, aprobada): título, metadato,
 * resumen en 3 líneas con «Ver más», frase literal, «Abrir» (panel de fuente) y «Fuente» (pestaña nueva).
 * La ficha va en un portal con posición fija para que el borde de la columna o el scroll de la lista no la corten.
 * El estado de verificación no se muestra hasta que el verificador sea confiable (D-11, 2026-10-06).
 */
export default function CitationLink({ citation, index, onClick }: CitationLinkProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const [recortado, setRecortado] = useState(false);
  const resumenRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLSpanElement>(null);
  const cardId = useId();

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const openSoon = (ms: number) => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(true), ms);
  };
  const closeSoon = () => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  const closeNow = useCallback((returnFocus: boolean) => {
    clearTimer();
    setOpen(false);
    if (returnFocus) chipRef.current?.focus();
  }, []);

  useEffect(() => clearTimer, []);
  useEffect(() => { if (!open) setExpanded(false); }, [open]);

  // «Ver más» solo si el resumen de verdad pasa de 3 líneas; sin medidas (jsdom), por largo.
  useLayoutEffect(() => {
    const el = resumenRef.current;
    if (!open || !el || expanded) return;
    setRecortado(el.clientHeight > 0 ? el.scrollHeight > el.clientHeight + 1 : (el.textContent || '').length > RESUMEN_LARGO);
  }, [open, expanded, citation.resumen]);

  // Debajo del número (como la maqueta); arriba si no cabe. Horizontalmente, dentro de la ventana.
  useLayoutEffect(() => {
    if (!open || !chipRef.current) return;
    const r = chipRef.current.getBoundingClientRect();
    // Dentro de la columna de lectura (como la maqueta); sin columna, dentro de la ventana.
    const col = chipRef.current.closest('[data-chat-column]')?.getBoundingClientRect();
    const right = Math.min(window.innerWidth - EDGE, col && col.width > 0 ? col.right : Infinity);
    const left = Math.max(EDGE, Math.min(r.left - 24, right - CARD_WIDTH));
    const h = cardRef.current?.offsetHeight || 260;
    if (r.bottom + GAP + h > window.innerHeight - EDGE && r.top - GAP - h > EDGE) {
      setPos({ left, bottom: window.innerHeight - r.top + GAP });
    } else {
      setPos({ left, top: r.bottom + GAP });
    }
  }, [open, expanded]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeNow(true); };
    const onScroll = (e: Event) => { if (!cardRef.current?.contains(e.target as Node)) closeNow(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, closeNow]);

  // Teclado: Tab desde el número entra a la ficha; Tab desde su último botón sigue con la página; Shift+Tab desde el
  // primero vuelve al número.
  const onChipKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || e.shiftKey || !open) return;
    const first = cardRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    if (first) { e.preventDefault(); clearTimer(); first.focus(); }
  };
  const onCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const items = Array.from(cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || []);
    if (e.shiftKey && document.activeElement === items[0]) {
      e.preventDefault();
      chipRef.current?.focus();
    } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
      e.preventDefault();
      const chip = chipRef.current; const cardEl = cardRef.current;
      closeNow(false);
      focusAfter(chip, cardEl);
    }
  };

  const onBlurWithin = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && (chipRef.current?.contains(next) || cardRef.current?.contains(next))) return;
    closeSoon();
  };

  const tipo = formatTipoLabel(citation.tipo);
  const meta = [tipo, formatCitationDate(citation.fecha) || citation.year].filter(Boolean).join(' · ');
  const resumen = citation.resumen?.trim();
  const sourceUrl = safeExternalUrl(citation.source_url);

  const card = open && (
    <span
      ref={cardRef}
      id={cardId}
      role="dialog"
      tabIndex={-1}
      aria-label={citation.titulo || `Fuente ${index + 1}`}
      onMouseEnter={clearTimer}
      onMouseLeave={closeSoon}
      onBlur={onBlurWithin}
      onKeyDown={onCardKeyDown}
      style={{ position: 'fixed', width: CARD_WIDTH, left: pos?.left ?? -9999, top: pos?.top, bottom: pos?.bottom, visibility: pos ? 'visible' : 'hidden' }}
      className="z-50 block bg-white rounded-[10px] px-4 py-3.5 text-left font-sans not-prose outline-none shadow-[0_12px_32px_rgba(15,23,42,0.12),0_0_0_1px_rgba(15,23,42,0.06)]"
    >
      {citation.titulo && <span className="block text-[13px] font-semibold leading-[1.35] text-slate-900">{citation.titulo}</span>}
      {meta && <span className="block text-[11.5px] leading-normal text-slate-500 mt-0.5 tabular-nums">{meta}</span>}
      {resumen && (
        <>
          <span ref={resumenRef} className={`text-[12.5px] leading-normal text-slate-500 mt-2 ${expanded ? 'block' : 'line-clamp-3'}`}>{resumen}</span>
          {(recortado || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded(x => !x)}
              className="text-xs leading-normal text-indigo-600 hover:text-indigo-700 mt-0.5 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 rounded"
            >
              {expanded ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </>
      )}
      {citation.excerpt && (
        <span className="block mt-2.5 border-l-2 border-indigo-200 py-0.5 pl-2.5 text-[12.5px] leading-normal text-slate-600">
          «{citation.excerpt}»
        </span>
      )}
      <span className="flex gap-1.5 mt-3">
        <button
          type="button"
          onClick={() => { closeNow(false); onClick(citation); }}
          className="inline-flex items-center gap-[5px] h-7 px-2.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Abrir
        </button>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-[5px] h-7 px-2.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 no-underline hover:bg-slate-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Fuente
            <ExternalLink className="w-[13px] h-[13px]" strokeWidth={1.8} aria-hidden="true" />
          </a>
        )}
      </span>
    </span>
  );

  return (
    <>
      <button
        ref={chipRef}
        type="button"
        onClick={() => onClick(citation)}
        onMouseEnter={() => openSoon(OPEN_DELAY_MS)}
        onMouseLeave={closeSoon}
        onFocus={() => openSoon(0)}
        onBlur={onBlurWithin}
        onKeyDown={onChipKeyDown}
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        aria-label={`Fuente ${index + 1}${citation.titulo ? `: ${citation.titulo}` : ''}`}
        className={`inline-flex items-center justify-center min-w-[17px] h-[17px] px-1 ml-[3px] rounded-[5px] text-[10.5px] font-medium leading-none align-[2px] tabular-nums cursor-pointer transition-colors motion-reduce:transition-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
          open ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
        }`}
      >
        {index + 1}
      </button>
      {card && createPortal(card, document.body)}
    </>
  );
}
